import { useMutation, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import {
  getFeedIdentityGeneration,
  mergeConfirmedFeedPost,
  scheduleFeedReconciliation,
} from "@bw/shared/hooks";
import type { PostResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/**
 * Compose-a-post mutation for a community's feed (Slice 3, WRITE). Posts
 * `createPost(communityId, { content })` and, on success, merges the confirmed
 * server response into an already-loaded community feed while scheduling
 * bounded projection reconciliation for every affected Feed scope.
 *
 * No optimistic update: the cache is NOT mutated before the server confirms — no
 * client-side prepend of a phantom post, no local count bump. A rejected create
 * (403 post_permission / network) leaves the feed exactly as the server last
 * reported it and surfaces on `mutation.error` for the page to show honestly
 * (and to retain the typed content for a retry).
 *
 * Re-entrancy is the page's concern: it disables Submit while `isPending` so a
 * double-click fires the mutation once. The content guard (required + trimmed)
 * is also the page's concern — this hook sends whatever content it is handed.
 */
export function useCreatePost(communityId: string) {
  const queryClient = useQueryClient();

  return useMutation<PostResponse, unknown, string, number>({
    onMutate: () => getFeedIdentityGeneration(queryClient),
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (content: string) =>
      communityApi.createPost(communityId, { content }),
    onSuccess: (post, _content, generation) => {
      if (generation !== getFeedIdentityGeneration(queryClient)) return;
      mergeConfirmedFeedPost(queryClient, post, [
        { scope: "community", subject: communityId },
      ]);
      scheduleFeedReconciliation(queryClient, [
        { scope: "community", subject: communityId },
        { scope: "home" },
        { scope: "communities" },
        { scope: "user", subject: post.author_id },
      ]);
      void queryClient.invalidateQueries({ queryKey: queryKeys.social.all });
    },
  });
}
